import {
  CellData,
  ExpandedState,
  flexRender,
  functionalUpdate,
  OnChangeFn,
  RowData,
  RowSelectionState,
  useTable,
} from "@tanstack/react-table";
import { dataTableFeatures } from "@/lib/Components/DataTable/tableFeatures";
import type {
  Column,
  ColumnDef,
  ReactTable,
} from "@/lib/Components/DataTable/types";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { PaginationFieldsFragment } from "@/gql/graphql";
import {
  getQueryKey,
  useGqlQuery,
  useOrgId,
  Variables,
} from "@/lib/GraphQLCodegen/fetcher";
import { keepPreviousData } from "@tanstack/react-query";
import { Button, Button as CatalystButton } from "@/components/catalyst/button";
import {
  Fragment,
  HTMLAttributes,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  CaretSortIcon,
} from "@radix-ui/react-icons";

import { cn } from "@/lib/utils";
import { DataTableColumnOptions } from "@/lib/Components/DataTable/DataTableColumnOptions";
import {
  AlertCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
} from "lucide-react";
import { useLocalStorage } from "@/lib/Hooks/useLocalStorage";
import { useDebouncedValue } from "@/lib/Hooks/useDebouncedValue";
import { useDebouncedCallback } from "@/lib/Hooks/useDebouncedCallback";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { EllipsisVerticalIcon } from "@heroicons/react/20/solid";
import { ActionsHook } from "@/lib/Components/Actions/ActionGroupButtons";
import { ActionMenu } from "@/lib/Components/Menu/ActionMenu";
import { flattenActions } from "@/lib/Components/Actions/utils/flattenActions";
import { Skeleton } from "@/components/ui/skeleton";
import { Input, InputGroup } from "@/components/catalyst/input";
import { MagnifyingGlassIcon } from "@heroicons/react/16/solid";
import type { TableId } from "@/app/Common/Utils/table-id";
import { useDragToScroll } from "@/lib/Hooks/useDragToScroll";
import { useTranslations } from "use-intl";
import { Select } from "@/components/catalyst/select";

// Every value handed to `useTable` is a memo dep somewhere in the row-model
// pipeline, and a changed dep can trigger an auto-reset that writes back into
// React state. Fresh literals per render turn that into an infinite loop, so
// the placeholders live at module scope and keep one identity forever.
const EMPTY_ROWS: never[] = [];
const EMPTY_SORTING: SortingState<string> = [];
const EMPTY_COLUMN_VISIBILITY: Record<string, boolean> = {};
const EMPTY_PAGINATOR: PaginationFieldsFragment = {
  lastPage: 1,
  total: 0,
  currentPage: 1,
  hasMorePages: false,
  count: 0,
  perPage: 10,
};

/** `expanded` is either the expand-all flag or a map of row id -> expanded. */
function isSameExpanded(a: ExpandedState, b: ExpandedState) {
  if (a === b) return true;
  if (typeof a === "boolean" || typeof b === "boolean") return false;

  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);

  return (
    aKeys.length === bKeys.length && aKeys.every((key) => a[key] === b[key])
  );
}

export type DataTableColDef<
  TData extends RowData,
  TValue extends CellData,
  TColumn extends string,
> = ColumnDef<TData, TValue> & {
  id: TColumn;
  /** Name shown in the column menu. Falls back to a prettified column id. */
  label?: string;
  /** Removes the column entirely — not toggleable. */
  isHidden?: boolean;
  /** Column starts toggled off; the user can still turn it on. */
  isInitiallyHidden?: boolean;
};

export type SortingState<TColumn> = {
  desc: boolean;
  id: TColumn;
}[];

export type GetQueryVariablesFn<
  TVariables extends Variables | undefined,
  TColumn extends string,
> = ({
  pagination,
  sorting,
  search,
}: {
  pagination: {
    pageIndex: number;
    pageSize: number;
  };
  sorting: {
    desc: boolean;
    id: TColumn;
  }[];
  search: string;
}) => TVariables;
interface DataTableProps<
  TQuery,
  TVariables extends Variables | undefined,
  TColumn extends string,
  TData extends RowData,
> {
  // `any` cell value: v9 makes `TValue` invariant on `ColumnDef`, so a single
  // table generic can't cover columns whose accessors return different types.
  columns: DataTableColDef<TData, any, TColumn>[];
  document: TypedDocumentNode<TQuery, TVariables>;
  getQueryVariables: GetQueryVariablesFn<TVariables, TColumn>;
  accessor: (data: TQuery) => {
    data: TData[];
    paginatorInfo: PaginationFieldsFragment;
  };
  title?: string;
  rightButtons?: ReactNode | ((table: ReactTable<TData>) => ReactNode);
  filters?: ReactNode | ((table: ReactTable<TData>) => ReactNode);
  id: TableId;
  hiddenColumns?: TColumn[];
  initialSorting?: SortingState<TColumn>;
  searchable?: boolean;
  searchPlaceholder?: string;
  /** 1-based. Controls pagination when supplied with `onPageChange`. */
  page?: number;
  onPageChange?: (page: number) => void;
  /** Controls the search box when supplied with `onSearchChange`. */
  search?: string;
  onSearchChange?: (search: string) => void;
  paginated?: boolean;
  columnToggles?: boolean;
  getActions?: NoInfer<ActionsHook<TData>>;
  onRetry?: () => void;
  errorMessage?: string;
  showErrorDetails?: boolean;
  emptyState?: ReactNode;
  activeFilterCount?: number;
  onClearFilters?: () => void;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  getRowId?: (row: TData, index: number) => string;
  /**
   * Renders an expandable detail panel under each row, behind a chevron in a
   * leading column. Rows without detail return null and get no chevron.
   */
  renderSubRow?: (row: TData) => ReactNode;
}

export function DataTable<
  TQuery,
  TVariables extends Variables | undefined,
  TColumn extends string,
  TData extends RowData,
>({
  columns,
  document,
  accessor,
  getQueryVariables,
  title,
  rightButtons,
  filters,
  id,
  hiddenColumns,
  initialSorting = [],
  searchable = true,
  searchPlaceholder,
  page,
  onPageChange,
  search: searchProp,
  onSearchChange,
  columnToggles = true,
  paginated = true,
  getActions,
  onRetry,
  errorMessage,
  showErrorDetails = false,
  emptyState,
  activeFilterCount = 0,
  onClearFilters,
  rowSelection,
  onRowSelectionChange,
  getRowId,
  renderSubRow,
}: DataTableProps<TQuery, TVariables, TColumn, TData>) {
  "use no memo";
  const t = useTranslations("common.dataTable");
  const { isAdmin } = useTenant();

  const [sorting, setSorting] = useLocalStorage(
    `table-${id}-sorting`,
    initialSorting,
  );

  const columnIds = columns.map((column) => [
    column.id,
    !column.isInitiallyHidden,
  ]);
  const initialHiddenColumns = Object.fromEntries(columnIds);
  hiddenColumns?.forEach((column) => {
    initialHiddenColumns[column] = false;
  });

  const [columnVisibility, setColumnVisibility] = useLocalStorage(
    `table-${id}-columns`,
    initialHiddenColumns,
  );

  const [storedColumnOrder, setStoredColumnOrder] = useLocalStorage<string[]>(
    `table-${id}-column-order`,
  );

  const [pageSize, setPageSize] = useLocalStorage(`table-${id}-page-size`, 10);

  const [localPagination, setLocalPagination] = useState({
    pageIndex: 0, //initial page index
    pageSize: pageSize ?? 10, //default page size
  });

  const pageIndex = page !== undefined ? page - 1 : localPagination.pageIndex;

  const pagination = useMemo(
    () => ({ pageIndex, pageSize: localPagination.pageSize }),
    [pageIndex, localPagination.pageSize],
  );

  const setPagination = (
    updater: (prev: { pageIndex: number; pageSize: number }) => {
      pageIndex: number;
      pageSize: number;
    },
  ) => {
    const next = updater(pagination);
    setLocalPagination(next);

    if (next.pageIndex !== pagination.pageIndex) {
      onPageChange?.(next.pageIndex + 1);
    }
  };

  useEffect(() => {
    setPageSize(localPagination.pageSize);
  }, [localPagination.pageSize]);

  // The owner only hears the search after a pause, so it can mirror it to the
  // URL without a navigation per keystroke.
  const [search, setSearch] = useState(searchProp ?? "");
  const [pushedSearch, setPushedSearch] = useState(searchProp ?? "");
  const debouncedSearch = useDebouncedValue(search, 300);

  if (searchProp !== undefined && searchProp !== pushedSearch) {
    setPushedSearch(searchProp);
    setSearch(searchProp);
  }

  const pushSearch = useDebouncedCallback((value: string) => {
    setPushedSearch(value);
    onSearchChange?.(value);
  }, 300);

  const handleSearchChange = (value: string) => {
    setSearch(value);

    // A controlled owner holds search and page together and resets the page
    // itself; resetting it here as well would race its update.
    if (onSearchChange) {
      pushSearch(value);
    } else {
      setLocalPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  };

  const [expanded, setExpanded] = useState<ExpandedState>({});

  // Drag to scroll functionality
  const {
    ref: scrollContainerRef,
    isDragging,
    dragHandlers,
  } = useDragToScroll<HTMLDivElement>();

  const variables = getQueryVariables({
    pagination: {
      pageSize: pagination.pageSize,
      pageIndex: pagination.pageIndex + 1, //graphql pagination starts at 1
    },
    sorting: sorting ?? [],
    search: debouncedSearch,
  });

  const {
    data: res,
    error,
    refetch,
    isFetchedAfterMount,
    dataUpdatedAt,
    isFetching,
    isLoading,
  } = useGqlQuery(document, variables, {
    placeholderData: keepPreviousData,
  });
  const orgId = useOrgId();

  useEffect(() => {
    if (import.meta.env.DEV && dataUpdatedAt === 0) {
      console.warn(
        "This table was not pre-loaded in the route",
        getQueryKey(document, variables, orgId),
        isFetchedAfterMount,
      );
    }
  }, []);

  const memoizedColumns = useMemo(() => {
    return columns.filter((c) => !c.isHidden);
  }, [isAdmin]);

  // A stored order can predate a column being added or removed, so drop ids the
  // table no longer has and slot new ones back where they were defined.
  const columnOrder = useMemo(() => {
    const definedOrder: string[] = memoizedColumns.map((column) => column.id);

    if (!storedColumnOrder?.length) {
      return definedOrder;
    }

    const order = storedColumnOrder.filter((columnId) =>
      definedOrder.includes(columnId),
    );

    definedOrder.forEach((columnId, index) => {
      if (!order.includes(columnId)) {
        order.splice(index, 0, columnId);
      }
    });

    return order;
  }, [memoizedColumns, storedColumnOrder]);

  const hasError = !!error;

  const { data, paginatorInfo } = res
    ? accessor(res)
    : { data: EMPTY_ROWS as TData[], paginatorInfo: EMPTY_PAGINATOR };

  const isSearching = search.length > 0;

  const table = useTable({
    features: dataTableFeatures,
    data,
    columns: memoizedColumns,
    ...(getRowId ? { getRowId } : {}),
    ...(onRowSelectionChange ? { onRowSelectionChange } : {}),
    manualPagination: true,
    onPaginationChange: (updaterOrValue) => {
      setPagination((prev) => functionalUpdate(updaterOrValue, prev));
    },
    pageCount: paginatorInfo.lastPage,
    rowCount: paginatorInfo.total,
    manualSorting: true,
    enableSorting: !isSearching,
    // Rows are flat, so `getCanExpand` would be false and swallow every toggle.
    // The detail panel decides per row whether it has anything to show.
    getRowCanExpand: () => !!renderSubRow,
    // The table re-resets `expanded` on every data change, always with a fresh
    // object. Storing that as-is would re-render for no change.
    onExpandedChange: (updaterOrValue) => {
      setExpanded((prev) => {
        const next = functionalUpdate(updaterOrValue, prev);
        return isSameExpanded(prev, next) ? prev : next;
      });
    },
    onColumnVisibilityChange: (updaterOrValue) => {
      const newColumnVisibility = functionalUpdate(
        updaterOrValue,
        columnVisibility ?? {},
      );
      setColumnVisibility(newColumnVisibility);
    },
    onColumnOrderChange: (updaterOrValue) => {
      setStoredColumnOrder(functionalUpdate(updaterOrValue, columnOrder));
    },
    onSortingChange: (sortingUpdater) => {
      const newSortVal = functionalUpdate(sortingUpdater, sorting ?? []);
      setSorting(newSortVal as any);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    state: {
      pagination,
      sorting: isSearching ? EMPTY_SORTING : (sorting ?? EMPTY_SORTING),
      columnVisibility: columnVisibility ?? EMPTY_COLUMN_VISIBILITY,
      columnOrder,
      expanded,
      ...(rowSelection !== undefined ? { rowSelection } : {}),
    },
    meta: {
      search,
    },
  });

  const columnCount =
    columns.length + (getActions ? 1 : 0) + (renderSubRow ? 1 : 0);

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      refetch();
    }
  };

  const getErrorMessage = () => {
    if (errorMessage) return errorMessage;
    if (error) {
      // Handle different types of errors
      if (error instanceof Error) {
        return error.message;
      }
      if (typeof error === "string") {
        return error;
      }

      return "An unexpected error occurred while loading data";
    }
    return "An error occurred";
  };

  return (
    <div>
      <div className="flex w-full flex-col gap-2 py-2 sm:flex-row sm:items-center">
        {title ? (
          <h2 className="text-lg font-medium leading-6 text-gray-900">
            {title}
          </h2>
        ) : null}

        {searchable ? (
          <InputGroup>
            <MagnifyingGlassIcon />
            <Input
              placeholder={searchPlaceholder ?? t("searchPlaceholder")}
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              disabled={hasError}
            />
          </InputGroup>
        ) : null}
        {columnToggles ? (
          <DataTableColumnOptions
            table={table}
            disabled={hasError}
            onReset={() => {
              setColumnVisibility(initialHiddenColumns);
              setStoredColumnOrder(undefined);
            }}
          />
        ) : null}
        {!isSearching
          ? typeof filters === "function"
            ? filters(table)
            : filters
          : null}
        <span className="hidden sm:block sm:grow" />
        {typeof rightButtons === "function"
          ? rightButtons(table)
          : rightButtons}
      </div>

      {isFetching && !hasError ? (
        <div className="absolute left-0 top-0 z-10 h-[2px] w-full overflow-hidden">
          <div className="animate-slide h-full w-1/3 bg-gradient-to-r from-transparent via-blue-500 to-transparent" />
        </div>
      ) : null}

      <div
        ref={scrollContainerRef}
        className={cn(
          "isolate overflow-x-auto rounded-md border border-gray-200",
          isDragging && "cursor-grabbing select-none",
        )}
        {...dragHandlers}
        style={{
          transform: "translateZ(0)",
          willChange: "transform",
          backfaceVisibility: "hidden",
          perspective: 1000,
        }}
      >
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {renderSubRow ? <th className="w-8" /> : null}

                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  );
                })}

                {getActions ? <th /> : null}
              </TableRow>
            ))}
          </TableHeader>

          {isLoading && !hasError ? (
            <tbody className="">
              {Array.from({ length: 2 }).map((i, idx) => {
                return (
                  <tr key={idx}>
                    <td colSpan={columnCount} className="p-2">
                      <Skeleton className="h-16 w-full" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          ) : hasError ? (
            <tbody>
              <TableRow>
                <TableCell colSpan={columnCount} className="h-32 text-center">
                  <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
                  <p className="mt-3 text-sm font-medium text-gray-900">
                    {t("unableToLoadData")}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    {showErrorDetails ? getErrorMessage() : t("pleaseTryAgain")}
                  </p>
                  <div className="mt-3">
                    <Button plain onClick={handleRetry} disabled={isFetching}>
                      {isFetching ? (
                        <RefreshCw className="mr-1 h-3 w-3 animate-spin" />
                      ) : (
                        <RefreshCw className="mr-1 h-3 w-3" />
                      )}
                      {t("tryAgain")}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            </tbody>
          ) : (
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => {
                  const subRow = renderSubRow?.(row.original);

                  return (
                    <Fragment key={row.id}>
                      <TableRow data-state={row.getIsSelected() && "selected"}>
                        {renderSubRow ? (
                          <TableCell className="w-8 px-2">
                            {subRow ? (
                              <button
                                type="button"
                                aria-label={
                                  row.getIsExpanded()
                                    ? t("collapseRow")
                                    : t("expandRow")
                                }
                                aria-expanded={row.getIsExpanded()}
                                className="rounded-md p-1 hover:bg-gray-50 active:bg-gray-100"
                                onClick={() => row.toggleExpanded()}
                              >
                                <ChevronDown
                                  className={cn(
                                    "h-4 w-4 text-gray-500 transition-transform",
                                    !row.getIsExpanded() && "-rotate-90",
                                  )}
                                />
                              </button>
                            ) : null}
                          </TableCell>
                        ) : null}

                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </TableCell>
                        ))}

                        {getActions ? (
                          <TableCell
                            className="table-actions sticky -right-px max-w-10 bg-white px-2"
                            width={40}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ActionMenu
                              button={
                                <div className="rounded-md p-1 ring-gray-200 hover:bg-gray-50 hover:ring-1 active:bg-gray-100 group-hover:shadow-sm">
                                  <EllipsisVerticalIcon className="h-4 w-4 text-gray-500" />
                                </div>
                              }
                              actionGroups={flattenActions(
                                getActions(row.original),
                              )}
                            />
                          </TableCell>
                        ) : null}
                      </TableRow>

                      {subRow && row.getIsExpanded() ? (
                        <TableRow className="hover:bg-transparent">
                          <TableCell
                            colSpan={columnCount}
                            className="bg-gray-50 p-0"
                          >
                            {subRow}
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </Fragment>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={columnCount} className="h-24 text-center">
                    {emptyState ?? (
                      <>
                        <p className="text-sm font-medium text-gray-900">
                          {t("noResultsFound")}
                        </p>
                        {onClearFilters && activeFilterCount > 0 ? (
                          <p className="mt-1 text-xs text-gray-500">
                            {t("filtersApplied", { count: activeFilterCount })}{" "}
                            <button
                              type="button"
                              className="font-medium text-blue-600 underline hover:text-blue-800"
                              onClick={() => {
                                onClearFilters();
                                table.setPagination((prev) => ({
                                  ...prev,
                                  pageIndex: 0,
                                }));
                              }}
                            >
                              {t("clearFilters")}
                            </button>
                          </p>
                        ) : (
                          <p className="mt-1 text-xs text-gray-500">
                            {t("noResultsDescription")}
                          </p>
                        )}
                      </>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          )}
        </Table>
      </div>

      {!hasError && paginated ? <DataTablePagination table={table} /> : null}
    </div>
  );
}

interface DataTablePaginationProps<TData extends RowData> {
  table: ReactTable<TData>;
}

export function DataTablePagination<TData extends RowData>({
  table,
}: DataTablePaginationProps<TData>) {
  "use no memo";
  const t = useTranslations("common.dataTable");
  const pagination = table.state.pagination;
  const rowCount = table.getRowCount();

  const b = (chunks: React.ReactNode) => (
    <span className="font-semibold text-gray-900">{chunks}</span>
  );
  const bMedium = (chunks: React.ReactNode) => (
    <span className="font-medium text-gray-900">{chunks}</span>
  );

  // Fix the issue when there are no results
  const start =
    rowCount === 0 ? 0 : pagination.pageIndex * pagination.pageSize + 1;
  const end = Math.min(
    (pagination.pageIndex + 1) * pagination.pageSize,
    rowCount,
  );

  // Calculate page numbers for display
  const currentPage = pagination.pageIndex + 1;
  const totalPages = table.getPageCount();

  return (
    <div className="flex flex-col gap-3 px-2 py-3 sm:flex-row sm:items-center sm:justify-between">
      {/* Left side - Results info and rows per page */}
      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-700">
        {/* Results count */}
        <span className="whitespace-nowrap">
          {t.rich("showingResults", {
            start,
            end,
            total: rowCount,
            b,
          })}
        </span>

        {/* Rows per page - hidden on smallest screens */}
        <div className="hidden items-center gap-2 sm:flex">
          <label htmlFor="rows-per-page" className="sr-only whitespace-nowrap">
            {t("rowsPerPage")}
          </label>
          <Select
            id="rows-per-page"
            value={`${pagination.pageSize}`}
            onChange={(e) => {
              table.setPageSize(Number(e.target.value));
            }}
          >
            {[10, 20, 50, 100].map((pageSize) => (
              <option key={pageSize} value={`${pageSize}`}>
                {pageSize}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {/* Right side - Page info and navigation */}
      <div className="flex items-center gap-3">
        {/* Page indicator */}
        <span className="whitespace-nowrap text-sm text-gray-700">
          {t.rich("pageInfo", {
            current: currentPage,
            total: totalPages,
            bMedium,
          })}
        </span>

        {/* Navigation buttons */}
        <div className="flex items-center gap-1">
          {/* Previous button */}
          <Button
            outline
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <ChevronLeft className="h-4 w-4 sm:mr-1" />
            <span className="hidden sm:inline">{t("previous")}</span>
          </Button>

          {/* Next button */}
          <Button
            outline
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <span className="hidden sm:inline">{t("next")}</span>
            <ChevronRight className="h-4 w-4 sm:ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}

interface DataTableColumnHeaderProps<
  TData extends RowData,
  TValue extends CellData,
> extends HTMLAttributes<HTMLDivElement> {
  column: Column<TData, TValue>;
  title: string;
}

export function DataTableColumnHeader<
  TData extends RowData,
  TValue extends CellData,
>({ column, title, className }: DataTableColumnHeaderProps<TData, TValue>) {
  "use no memo";

  if (!column.getCanSort()) {
    return <div className={cn(className)}>{title}</div>;
  }

  return (
    <CatalystButton
      plain
      onClick={() => {
        column.toggleSorting();
      }}
      className={cn("-translate-x-3 text-gray-500", className)}
    >
      <span className="whitespace-nowrap">{title}</span>
      {column.getIsSorted() === "desc" ? (
        <ArrowDownIcon className="ml-2 h-4 w-4 shrink-0" />
      ) : column.getIsSorted() === "asc" ? (
        <ArrowUpIcon className="ml-2 h-4 w-4 shrink-0" />
      ) : (
        <CaretSortIcon className="ml-2 h-4 w-4 shrink-0" />
      )}
    </CatalystButton>
  );
}
